// A database's storage (System > database > Storage): NextAdmin `tables/basic-tables` in cards — largest tables and
// their growth, schemas, compression, unused indexes and dead rows, from the agents' hourly snapshot.
import { Badge, Card, DataTable, type DataTableColumn } from '../../../components/ui/index.js';
import type { SystemDatabase } from '../../../generated/api/types.gen.js';
import { formatBytes, formatCount } from './metric-format.js';

type Relation = SystemDatabase['relations'][number];

const RELATION_COLUMNS: DataTableColumn<Relation>[] = [
  { key: 'name', header: 'Table', render: (r) => <span className="font-mono text-xs">{r.name}</span> },
  {
    key: 'kind',
    header: 'Kind',
    render: (r) => <Badge variant={r.kind === 'hypertable' ? 'teal' : 'neutral'}>{r.kind}</Badge>,
  },
  {
    key: 'bytes',
    header: 'Size',
    align: 'right',
    sortable: true,
    sortValue: (r) => r.bytes,
    render: (r) => formatBytes(r.bytes),
  },
  {
    key: 'growth',
    header: 'Growth a day',
    align: 'right',
    sortable: true,
    sortValue: (r) => r.growthBytesPerDay ?? 0,
    render: (r) =>
      r.growthBytesPerDay === null ? '—' : `${r.growthBytesPerDay >= 0 ? '+' : ''}${formatBytes(r.growthBytesPerDay)}`,
  },
];

const NAMED_COLUMNS: DataTableColumn<{ name: string; bytes: number }>[] = [
  { key: 'name', header: 'Name', render: (n) => <span className="font-mono text-xs">{n.name}</span> },
  { key: 'bytes', header: 'Size', align: 'right', render: (n) => formatBytes(n.bytes) },
];

export function StorageCards({ database }: { database: SystemDatabase }) {
  return (
    <>
      <Card title="Largest tables" className="xl:col-span-12">
        <DataTable columns={RELATION_COLUMNS} rows={database.relations} rowKey={(r) => r.name} pageSize={25} />
      </Card>
      <Card title="Schemas" className="xl:col-span-6">
        <DataTable columns={NAMED_COLUMNS} rows={database.schemas} rowKey={(s) => s.name} />
      </Card>
      <Card title="Compression" className="xl:col-span-6">
        <DataTable
          columns={[
            { key: 'name', header: 'Hypertable', render: (c) => <span className="font-mono text-xs">{c.name}</span> },
            { key: 'before', header: 'Before', align: 'right', render: (c) => formatBytes(c.beforeBytes) },
            { key: 'after', header: 'After', align: 'right', render: (c) => formatBytes(c.afterBytes) },
            {
              key: 'ratio',
              header: 'Saved',
              align: 'right',
              render: (c) => (c.beforeBytes > 0 ? `${Math.round((1 - c.afterBytes / c.beforeBytes) * 100)} %` : '—'),
            },
            {
              key: 'chunks',
              header: 'Chunks',
              align: 'right',
              render: (c) =>
                c.chunks ? formatCount(c.chunks.compressed + c.chunks.partial + c.chunks.uncompressed) : '—',
            },
            {
              key: 'uncompressed',
              header: 'Holding uncompressed rows',
              align: 'right',
              // Partial = compressed, then written to; a chunk past the policy's horizon should hold none.
              render: (c) =>
                !c.chunks ? (
                  '—'
                ) : c.dueChunks > 0 ? (
                  <Badge variant="warning" dot>
                    {`${formatCount(c.dueChunks)} past the horizon`}
                  </Badge>
                ) : (
                  formatCount(c.chunks.partial + c.chunks.uncompressed)
                ),
            },
          ]}
          rows={database.compression}
          rowKey={(c) => c.name}
        />
      </Card>
      <Card title="Unused indexes (10 MB or more)" className="xl:col-span-6">
        <DataTable columns={NAMED_COLUMNS} rows={database.unusedIndexes} rowKey={(i) => i.name} />
      </Card>
      <Card title="Most dead rows" className="xl:col-span-6">
        <DataTable
          columns={[
            { key: 'name', header: 'Table', render: (d) => <span className="font-mono text-xs">{d.name}</span> },
            { key: 'count', header: 'Dead rows', align: 'right', render: (d) => formatCount(d.count) },
          ]}
          rows={database.deadTuples}
          rowKey={(d) => d.name}
        />
      </Card>
    </>
  );
}
